import type {
  KlarnaAuthorizationAddress,
  KlarnaAuthorizationCallbackResponse,
  KlarnaAuthorizationOptions,
  KlarnaLoadCallbackResponse,
  StandaloneKlarnaAuthorizationAddresses,
} from '~/models/checkout/KlarnaSDKInterfaces';
import {
  KLARNA_PARTPAYMENT_IDENTIFIER,
  KLARNA_PAY_LATER_IDENTIFIER,
  KLARNA_PAY_NOW_IDENTIFIER,
} from '~/models/checkout/PaymentMethods.model';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let Klarna: any;

export const useKlarna = () => {
  const notificationStore = useNotificationStore();
  const checkoutStore = useMyCheckoutStore();

  const selectedKlarnaPaymentMethodCategory = computed(() => {
    const selectedMethodAndIdentifier =
      checkoutStore.userState.payment
        .selectedPaymentMethodCommaSeparatedIdentifier;

    if (
      selectedMethodAndIdentifier &&
      selectedMethodAndIdentifier.includes(',') &&
      (selectedMethodAndIdentifier === KLARNA_PAY_NOW_IDENTIFIER ||
        selectedMethodAndIdentifier === KLARNA_PAY_LATER_IDENTIFIER ||
        selectedMethodAndIdentifier === KLARNA_PARTPAYMENT_IDENTIFIER)
    ) {
      return selectedMethodAndIdentifier.split(',')[1];
    }

    return '';
  });

  const initKlarna = async (clientToken?: string) => {
    try {
      if (Klarna.Payments.init === undefined) {
        return;
      }

      if (!clientToken) {
        throw new Error('clientToken not defined');
      }

      Klarna.Payments.init({
        client_token: clientToken,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const loadKlarna = (
    klarnaRoodDivId: string,
    paymentMethodCategoryIdentifier?: string,
    callback?: (res: any) => void,
  ) => {
    try {
      if (Klarna.Payments.init === undefined) {
        return;
      }

      if (!paymentMethodCategoryIdentifier) {
        return;
      }

      Klarna.Payments.load(
        {
          container: `#${klarnaRoodDivId}`,
          payment_method_category: paymentMethodCategoryIdentifier,
          instance_id: 'klarna-widget',
        },
        {},
        (res: KlarnaLoadCallbackResponse) => {
          if (res.show_form === false) {
            onShowFormFalse();
            return;
          }

          if (callback !== undefined) {
            callback(res);
          }
        },
      );
    } catch (e) {
      console.error(e);
    }
  };

  const authorizeKlarna = (
    callback: (res: KlarnaAuthorizationCallbackResponse) => void,
    standaloneData?: StandaloneKlarnaAuthorizationAddresses,
  ) => {
    const checkoutStore = useMyCheckoutStore();

    try {
      if (Klarna.Payments.init === undefined) {
        return;
      }

      const createCheckoutBillingAddress = (): KlarnaAuthorizationAddress => {
        return {
          given_name: checkoutStore.fetchedData.address?.FirstName,
          family_name: checkoutStore.fetchedData.address?.LastName,
          email: checkoutStore.userState.newEmailAddress,
          street_address: checkoutStore.fetchedData.address?.Street,
          postal_code:
            checkoutStore.fetchedData.address?.ZipPostalCode?.replaceAll(
              ' ',
              '',
            ),
          city: checkoutStore.fetchedData.address?.City,
          phone: '',
          country: 'SE',
        };
      };

      const createCheckoutShippingAddress = (
        billingAddress: KlarnaAuthorizationAddress,
      ): KlarnaAuthorizationAddress => {
        const shippingAddress = {
          ...billingAddress,
        };

        const selectedPickupLocation = checkoutStore.selectedPickupLocation;

        if (selectedPickupLocation && selectedPickupLocation.Address) {
          shippingAddress.city = selectedPickupLocation.Address.City;
          shippingAddress.street_address =
            selectedPickupLocation.Address.Street;
          shippingAddress.postal_code =
            selectedPickupLocation.Address.Zip.replaceAll(' ', '');
        }

        return shippingAddress;
      };

      const billingAddress: KlarnaAuthorizationAddress = standaloneData
        ? standaloneData.BillingAddress
        : createCheckoutBillingAddress();

      const shippingAddress: KlarnaAuthorizationAddress = standaloneData
        ? standaloneData.ShippingAddress
        : createCheckoutShippingAddress(billingAddress);

      const authOptions: KlarnaAuthorizationOptions = { auto_finalize: true };
      if (selectedKlarnaPaymentMethodCategory.value) {
        authOptions.payment_method_category =
          selectedKlarnaPaymentMethodCategory.value;
      }

      Klarna.Payments.authorize(
        authOptions,
        {
          billing_address: {
            ...billingAddress,
          },
          shipping_address: {
            ...shippingAddress,
          },
          attachment: {
            content_type: 'application/vnd.klarna.internal.emd-v2+json',
            body: '{"other_delivery_address":[{"shipping_method":"pick-up point","shipping_type":"normal"}]}',
          },
          customer: {
            date_of_birth: '',
          },
        },
        (res: KlarnaAuthorizationCallbackResponse) => {
          callback(res);
        },
      );
    } catch (e) {
      console.log(e);
    }
  };

  const onShowFormFalse = () => {
    notificationStore.setErrorNotificationFromCatchClause(
      'Vi kan inte erbjuda Klarna för det här köpet. Du kan använda ett av de andra betalsätten istället!',
    );
    checkoutStore.ui.klarnaDeniedUserOrAvailability = true;
  };

  return {
    initKlarna,
    loadKlarna,
    authorizeKlarna,
    onShowFormFalse,
  };
};
